import './neighborhood-map.scss';
import Base from '../base/base';
import { loadMapboxGl, MapboxGL } from '../../shared/utils/mapbox';

/** How far the walking band reaches. Fixed: the section's promise is "10 minutes on foot". */
const WALK_MINUTES = 10;
const ISOCHRONE_URL = 'https://api.mapbox.com/isochrone/v1/mapbox/walking';

/** Inset kept between the band and the container edge when fitting */
const FIT_PADDING = 40;

/**
 * POI classes worth calling out, as named by the Mapbox Streets tileset. A
 * class the tileset does not carry simply never matches, so the layer thins
 * out rather than erroring — which is what we want from a decorative layer.
 */
const AMENITY_CLASSES = [
  'park_like',
  'education',
  'food_and_drink',
  'food_and_drink_stores',
  'arts_and_entertainment',
  'sport_and_leisure',
  'shopping'
];

/** Covers the globe. The band's rings become holes in it, so only the outside dims. */
const WORLD_RING: [number, number][] = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85]
];

interface NeighborhoodMapOptions {
  /** Selector for the map container element */
  selector?: string;
  /** Mapbox access token (required; the map is disabled without it) */
  accessToken?: string;
  /** Neighborhood name for the marker. Falls back to `data-neighborhood`. */
  name?: string;
  /** Latitude. Falls back to `data-lat`. */
  lat?: number;
  /** Longitude. Falls back to `data-long`, then `data-lng`. */
  lng?: number;
  /** Pin nearby parks, schools, food and shops from the base style. Default true. */
  amenities?: boolean;
  /** Draw the 10-minute walking band. Default true. */
  walkBand?: boolean;
  /** Dim everything outside that band. Default true. */
  spotlight?: boolean;
  /**
   * Zoom on wheel scroll. Off hands the wheel back to the page, so scrolling
   * past the map keeps scrolling; the +/- buttons, double-click and pinch still
   * zoom. Default true.
   */
  scrollZoom?: boolean;
  /**
   * Require ⌘/Ctrl + wheel to zoom, and two fingers to pan on touch, so a page
   * scroll that crosses the map keeps scrolling the page. Mapbox shows its own
   * hint overlay when a plain scroll is caught. Default false.
   */
  cooperativeGestures?: boolean;
  /** Mapbox style URL */
  mapStyle?: string;
  /** Zoom used when there is no band to fit to */
  zoom?: number;
  debug?: boolean;
  [key: string]: any;
}

interface NeighborhoodMapConfig {
  selector: string;
  accessToken: string;
  name: string;
  lat: number | null;
  lng: number | null;
  amenities: boolean;
  walkBand: boolean;
  spotlight: boolean;
  scrollZoom: boolean;
  cooperativeGestures: boolean;
  mapStyle: string;
  zoom: number;
}

/**
 * NeighborhoodMap
 *
 * A single-point Mapbox map for a neighborhood detail page: one labelled
 * marker on the neighborhood's coordinates, over three layers that each answer
 * "what is around here", and each of which can be switched off:
 *
 * - `walkBand` — the area reachable in a 10-minute walk, from Mapbox's
 *   Isochrone API.
 * - `spotlight` — everything outside that band dimmed, so the reachable area
 *   reads as the subject.
 * - `amenities` — parks, schools, food and shops pinned from the base style's
 *   own POI data, no extra request.
 *
 * The name and coordinates come from the container's `data-neighborhood`,
 * `data-lat` and `data-long` attributes, which Webflow binds per neighborhood.
 * Options override them, which is what the tests use.
 *
 * Two options keep the map from swallowing the scroll of someone reading past
 * it: `scrollZoom: false` drops wheel zoom outright, leaving the +/- buttons,
 * and `cooperativeGestures` keeps it behind ⌘/Ctrl at the cost of an overlay
 * hint. Both detail pages take the first; the index maps are their own
 * component and keep the default gestures.
 *
 * Mapbox GL loads from the CDN at runtime (`shared/utils/mapbox`), so no page
 * pays for it in the bundle.
 */
export default class NeighborhoodMap extends Base {
  static readonly CONTAINER_CLASS = 'nbm';
  static readonly BAND_SOURCE = 'nbm-walk';
  static readonly BAND_FILL_LAYER = 'nbm-walk-fill';
  static readonly BAND_LINE_LAYER = 'nbm-walk-line';
  static readonly SPOTLIGHT_LAYER = 'nbm-spotlight';
  static readonly AMENITY_LAYER = 'nbm-amenities';

  private config: NeighborhoodMapConfig;

  private container: HTMLElement | null = null;
  private mapEl: HTMLElement | null = null;
  private mapboxgl: MapboxGL = null;
  private map: any = null;
  private marker: any = null;

  private creating = false;
  private destroyed = false;

  constructor(options: NeighborhoodMapOptions = {}) {
    super(options.debug || false);

    this.config = {
      selector: '#map-explore-map',
      accessToken: '',
      name: '',
      lat: null,
      lng: null,
      amenities: true,
      walkBand: true,
      spotlight: true,
      scrollZoom: true,
      cooperativeGestures: false,
      mapStyle: 'mapbox://styles/mapbox/streets-v12',
      zoom: 14,
      ...options
    };

    this.init();
  }

  init(): void {
    this.container = document.querySelector<HTMLElement>(this.config.selector);
    if (!this.container) {
      this.log('Map container not found, skipping', this.config.selector);
      return;
    }
    if (!this.config.accessToken) {
      this.log('No Mapbox access token provided, map disabled');
      return;
    }

    this.readContainerData();

    if (this.config.lat === null || this.config.lng === null) {
      this.log('No usable coordinates, skipping', this.config.selector);
      return;
    }

    this.container.classList.add(NeighborhoodMap.CONTAINER_CLASS);
    this.ensureMap();

    this.log(`Initialized on ${this.config.name || 'unnamed neighborhood'}`, [
      this.config.lng,
      this.config.lat
    ]);
  }

  /**
   * Fill anything the caller left unset from the container's data attributes.
   * Webflow binds those per neighborhood, so they are the production source;
   * an explicit option wins, which is how the tests drive the component.
   */
  private readContainerData(): void {
    const data = this.container?.dataset;
    if (!data) return;

    if (!this.config.name) this.config.name = (data.neighborhood || '').trim();
    if (this.config.lat === null) this.config.lat = this.parseCoordinate(data.lat);
    if (this.config.lng === null) {
      this.config.lng = this.parseCoordinate(data.long || data.lng);
    }
  }

  private parseCoordinate(value?: string): number | null {
    if (!value) return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /* ===================
     Map lifecycle
     =================== */

  private async ensureMap(): Promise<void> {
    if (this.map || this.creating || this.destroyed || !this.container) return;
    this.creating = true;

    try {
      const mapboxgl = await loadMapboxGl();
      if (this.destroyed || !this.container || this.map) return;
      this.mapboxgl = mapboxgl;

      // Mapbox forces `position: relative` on its root, so it renders into a
      // child and the container keeps whatever the Webflow CSS gives it.
      this.mapEl = document.createElement('div');
      this.mapEl.className = 'nbm-canvas';
      this.container.appendChild(this.mapEl);

      mapboxgl.accessToken = this.config.accessToken;
      this.map = new mapboxgl.Map({
        container: this.mapEl,
        style: this.config.mapStyle,
        center: [this.config.lng, this.config.lat],
        zoom: this.config.zoom,
        scrollZoom: this.config.scrollZoom,
        cooperativeGestures: this.config.cooperativeGestures,
        attributionControl: false
      });
      this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      this.map.addControl(new mapboxgl.AttributionControl({ compact: true }));

      this.map.on('load', () => {
        if (this.destroyed) return;
        this.map.resize();
        this.addMarker();
        this.addAmenityLayer();
        this.addWalkLayers();
        this.log('Map ready');
      });
    } catch (error) {
      this.log('Mapbox failed to load; container keeps its placeholder', error);
    } finally {
      this.creating = false;
    }
  }

  /* ===================
     Marker
     =================== */

  private addMarker(): void {
    if (!this.map || !this.mapboxgl) return;

    const el = document.createElement('div');
    el.className = 'nbm-marker';
    el.textContent = this.config.name || 'Neighborhood';
    el.setAttribute('aria-label', this.config.name || 'Neighborhood');

    this.marker = new this.mapboxgl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([this.config.lng, this.config.lat])
      .addTo(this.map);
  }

  /* ===================
     Amenities
     =================== */

  /**
   * Pin the amenities the base style already knows about. This reads the
   * style's own `poi_label` data rather than calling an API, so it costs
   * nothing and stays in step with whatever the map is showing.
   */
  private addAmenityLayer(): void {
    if (!this.config.amenities || !this.map) return;

    // Styles other than Mapbox Streets may carry neither, and requesting a
    // missing source throws rather than no-oping
    if (!this.map.getSource || !this.map.getSource('composite')) {
      this.log('Base style has no composite source, skipping amenities');
      return;
    }

    this.map.addLayer({
      id: NeighborhoodMap.AMENITY_LAYER,
      type: 'circle',
      source: 'composite',
      'source-layer': 'poi_label',
      filter: ['match', ['get', 'class'], AMENITY_CLASSES, true, false],
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 3, 16, 7],
        'circle-color': '#f9aa43',
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.9
      }
    });
  }

  /* ===================
     Walking band + spotlight
     =================== */

  /**
   * Both layers read the same contour, so one request serves them and a page
   * that wants neither never makes it.
   */
  private async addWalkLayers(): Promise<void> {
    if (!this.config.walkBand && !this.config.spotlight) return;

    const contour = await this.fetchContour();
    if (!contour || this.destroyed || !this.map) return;

    this.map.addSource(NeighborhoodMap.BAND_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [contour] }
    });

    // Spotlight first, so the band's own fill reads above the dimming
    if (this.config.spotlight) {
      this.map.addLayer({
        id: NeighborhoodMap.SPOTLIGHT_LAYER,
        type: 'fill',
        source: { type: 'geojson', data: this.buildSpotlight(contour) },
        paint: { 'fill-color': '#0d2021', 'fill-opacity': 0.45 }
      });
    }

    if (this.config.walkBand) {
      this.map.addLayer({
        id: NeighborhoodMap.BAND_FILL_LAYER,
        type: 'fill',
        source: NeighborhoodMap.BAND_SOURCE,
        paint: { 'fill-color': '#3b8e90', 'fill-opacity': 0.18 }
      });
      this.map.addLayer({
        id: NeighborhoodMap.BAND_LINE_LAYER,
        type: 'line',
        source: NeighborhoodMap.BAND_SOURCE,
        paint: { 'line-color': '#1c4344', 'line-width': 2, 'line-opacity': 0.7 }
      });
    }

    this.fitToContour(contour);
  }

  /** The 10-minute walk polygon, or null if the request fails */
  private async fetchContour(): Promise<any | null> {
    const { lng, lat, accessToken } = this.config;
    const url =
      `${ISOCHRONE_URL}/${lng},${lat}` +
      `?contours_minutes=${WALK_MINUTES}&polygons=true&denoise=1&access_token=${accessToken}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Isochrone request failed: ${response.status}`);

      const body = await response.json();
      const feature = body?.features?.[0];
      if (!feature?.geometry?.coordinates?.length) {
        this.log('Isochrone response carried no polygon');
        return null;
      }
      return feature;
    } catch (error) {
      // The marker map is still worth having, so this stays a soft failure
      this.log('Walking band unavailable', error);
      return null;
    }
  }

  /**
   * A world-sized polygon carrying the contour's outer rings as holes, so
   * everything the walk cannot reach sits under the dimming and the band
   * itself stays clear.
   */
  private buildSpotlight(contour: any): any {
    const rings =
      contour.geometry.type === 'MultiPolygon'
        ? contour.geometry.coordinates.map((polygon: any) => polygon[0])
        : [contour.geometry.coordinates[0]];

    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [WORLD_RING, ...rings]
      }
    };
  }

  private fitToContour(contour: any): void {
    if (!this.map || !this.mapboxgl?.LngLatBounds) return;

    const bounds = new this.mapboxgl.LngLatBounds();
    const polygons =
      contour.geometry.type === 'MultiPolygon'
        ? contour.geometry.coordinates
        : [contour.geometry.coordinates];

    polygons.forEach((polygon: any) =>
      polygon[0].forEach((point: [number, number]) => bounds.extend(point))
    );

    this.map.fitBounds(bounds, {
      padding: FIT_PADDING,
      duration: this.prefersReducedMotion() ? 0 : 600
    });
  }

  private prefersReducedMotion(): boolean {
    return (
      (typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
      false
    );
  }

  /* ===================
     Cleanup
     =================== */

  destroy(): void {
    this.destroyed = true;

    this.marker?.remove();
    this.marker = null;

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    this.mapEl?.remove();
    this.mapEl = null;

    this.container?.classList.remove(NeighborhoodMap.CONTAINER_CLASS);
  }
}
